import React, { useEffect, useState, useMemo, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Receipt, RefreshCw, Loader2, CheckCircle2, XCircle, Download, Search, Archive,
  ArrowLeft, ArrowRight,
} from "lucide-react";

const BILLING_PAGE_SIZE = 15;

/**
 * Admin › Fatturazione — gestione delle fatture di vendita emesse per gli
 * acquisti di crediti. Ogni pagamento Stripe completato genera una fattura
 * `FATT-N-YYYY` con progressivo annuale. Da qui l'admin le può scaricare in
 * batch e segnare come "trasmesse" dopo averle inviate a SDI.
 */
export default function AdminBilling() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [filterYear, setFilterYear] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterYear) params.set("year", filterYear);
      if (filterStatus) params.set("status", filterStatus);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (search) params.set("search", search);
      const [r1, r2] = await Promise.all([
        api.get(`/admin/billing?${params.toString()}`),
        api.get("/admin/billing/summary"),
      ]);
      setItems(r1.data.items || []);
      setSummary(r2.data);
      setPage(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }, [filterYear, filterStatus, dateFrom, dateTo, search]);

  useEffect(() => { load(); }, [load]);

  const selectedIds = useMemo(() => [...selected], [selected]);
  const allSelected = items.length > 0 && items.every((it) => selected.has(it.id));

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((it) => it.id)));

  const bulkMark = async (status) => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Segnare ${selectedIds.length} fatture come "${status === "transmitted" ? "trasmesse" : "NON trasmesse"}"?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/billing/bulk/mark", { ids: selectedIds, status });
      toast.success(`${data.modified} fatture aggiornate`);
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento");
    } finally {
      setBusy(false);
    }
  };

  const bulkZip = async () => {
    if (!selectedIds.length) return;
    setBusy(true);
    try {
      const res = await api.post("/admin/billing/bulk/zip", { ids: selectedIds }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fatture-batch-${selectedIds.length}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore download ZIP");
    } finally {
      setBusy(false);
    }
  };

  const downloadOne = async (inv) => {
    try {
      const res = await api.get(`/admin/billing/${inv.id}/xml`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = inv.xml_filename || `${inv.full_number}.xml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore download");
    }
  };

  const backfill = null; // rimosso: causava emissione retroattiva non voluta

  const years = useMemo(() => Object.keys(summary?.by_year || {}).sort().reverse(), [summary]);

  const paged = useMemo(
    () => items.slice(page * BILLING_PAGE_SIZE, (page + 1) * BILLING_PAGE_SIZE),
    [items, page]
  );
  const totalPages = Math.max(1, Math.ceil(items.length / BILLING_PAGE_SIZE));

  return (
    <div data-testid="admin-billing">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight flex items-center gap-3">
            <Receipt className="w-7 h-7 text-emerald-700" /> Fatturazione clienti
          </h1>
          <p className="text-sm text-[#4A4A4A] mt-1">Fatture di vendita <span className="font-mono">FATT-N-AAAA</span> emesse automaticamente ad ogni acquisto crediti. Progressivo per anno solare, reset a gennaio.</p>
        </div>
      </div>

      {/* KPI */}
      {summary && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
          {years.slice(0, 3).map((y) => {
            const s = summary.by_year[y];
            const total = (s.transmitted || 0) + (s.not_transmitted || 0);
            return (
              <div key={y} className="rounded-2xl border border-[#E8E4D9] bg-white p-5">
                <div className="text-[10px] uppercase tracking-widest text-[#737373] font-bold">Anno {y}</div>
                <div className="mt-2 flex items-baseline gap-2">
                  <div className="font-display text-3xl font-extrabold">{total}</div>
                  <div className="text-xs text-[#737373]">fatture</div>
                </div>
                <div className="mt-3 flex items-center gap-3 text-xs">
                  <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3 h-3" />{s.transmitted || 0} trasm.</span>
                  <span className="inline-flex items-center gap-1 text-amber-700"><XCircle className="w-3 h-3" />{s.not_transmitted || 0} non trasm.</span>
                </div>
                <div className="mt-2 flex items-center gap-3 text-xs text-[#4A4A4A]">
                  <span>Lordo: <span className="font-mono">€ {s.gross.toFixed(2)}</span></span>
                  <span>IVA: <span className="font-mono">€ {s.vat.toFixed(2)}</span></span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filtri */}
      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search className="w-3 h-3 absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca N° fattura, cliente, P.IVA…"
            className="pl-8 pr-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm min-w-[280px]"
            data-testid="billing-search"
          />
        </div>
        <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} className="px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm" data-testid="billing-filter-year">
          <option value="">Tutti gli anni</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm" data-testid="billing-filter-status">
          <option value="">Tutti gli stati</option>
          <option value="not_transmitted">Non trasmesse</option>
          <option value="transmitted">Trasmesse</option>
        </select>
        <div className="flex items-center gap-1 text-xs text-[#737373]">
          <span className="uppercase tracking-wider font-bold">Data:</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-2 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm"
            data-testid="billing-date-from"
            title="Data dal"
          />
          <span>→</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-2 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm"
            data-testid="billing-date-to"
            title="Data al"
          />
          {(dateFrom || dateTo) && (
            <button
              onClick={() => { setDateFrom(""); setDateTo(""); }}
              className="text-[#737373] hover:text-[#0A0A0A] px-1"
              title="Pulisci intervallo date"
              data-testid="billing-date-clear"
            >×</button>
          )}
        </div>
        <button onClick={load} className="text-xs text-[#737373] hover:text-[#0A0A0A] inline-flex items-center gap-1 ml-auto" data-testid="billing-refresh">
          <RefreshCw className="w-3 h-3" /> Ricarica
        </button>
      </div>

      {/* Bulk actions bar */}
      {selectedIds.length > 0 && (
        <div className="mt-4 rounded-xl border border-[#0A0A0A] bg-[#0A0A0A] text-white p-3 flex items-center justify-between" data-testid="billing-bulk-bar">
          <div className="text-sm font-bold">{selectedIds.length} fatture selezionate</div>
          <div className="flex items-center gap-2">
            <button onClick={bulkZip} disabled={busy} className="px-3 py-1.5 rounded-md bg-white text-[#0A0A0A] text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="billing-bulk-zip">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Archive className="w-3 h-3" />}
              Scarica ZIP
            </button>
            <button onClick={() => bulkMark("transmitted")} disabled={busy} className="px-3 py-1.5 rounded-md bg-emerald-500 text-white text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="billing-bulk-mark-trasm">
              <CheckCircle2 className="w-3 h-3" /> Segna trasmesse
            </button>
            <button onClick={() => bulkMark("not_transmitted")} disabled={busy} className="px-3 py-1.5 rounded-md bg-amber-500 text-white text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="billing-bulk-mark-not">
              <XCircle className="w-3 h-3" /> Segna NON trasmesse
            </button>
            <button onClick={() => setSelected(new Set())} className="text-xs text-white/70 hover:text-white ml-1">Annulla</button>
          </div>
        </div>
      )}

      {/* Tabella */}
      <div className="mt-4 rounded-2xl border border-[#E8E4D9] bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F2EA] border-b border-[#E8E4D9]">
              <tr>
                <th className="px-3 py-2 text-left w-8">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="billing-select-all" />
                </th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-[#737373] font-bold">N° fattura</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-[#737373] font-bold">Data</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-[#737373] font-bold">Cliente</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-[#737373] font-bold">Pacchetto</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-[#737373] font-bold">Imponibile</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-[#737373] font-bold">IVA</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-[#737373] font-bold">Totale</th>
                <th className="px-3 py-2 text-center text-[10px] uppercase tracking-wider text-[#737373] font-bold">Stato</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} className="py-10 text-center text-[#737373]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Caricamento…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={10} className="py-10 text-center text-[#737373]">Nessuna fattura ancora emessa. Verrà creata automaticamente al primo acquisto crediti.</td></tr>
              )}
              {paged.map((inv) => (
                <tr key={inv.id} className="border-b border-[#E8E4D9]/60 hover:bg-[#F5F2EA]/40" data-testid={`billing-row-${inv.full_number}`}>
                  <td className="px-3 py-2">
                    <input type="checkbox" checked={selected.has(inv.id)} onChange={() => toggle(inv.id)} data-testid={`billing-select-${inv.full_number}`} />
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{inv.full_number}</td>
                  <td className="px-3 py-2 text-xs text-[#4A4A4A]">{inv.data}</td>
                  <td className="px-3 py-2">
                    <div className="text-xs font-bold">{inv.cliente?.denominazione || "—"}</div>
                    <div className="text-[10px] text-[#737373] font-mono">{inv.cliente?.partita_iva || inv.cliente?.codice_fiscale || "—"}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">{inv.package_name} <span className="text-[#737373]">({inv.credits} cred.)</span></td>
                  <td className="px-3 py-2 text-right font-mono text-xs">€ {inv.amount_net_eur?.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-[#737373]">€ {inv.vat_amount_eur?.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs font-bold">€ {inv.amount_gross_eur?.toFixed(2)}</td>
                  <td className="px-3 py-2 text-center">
                    {inv.status === "transmitted" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800"><CheckCircle2 className="w-3 h-3" /> Trasmessa</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"><XCircle className="w-3 h-3" /> Non trasmessa</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => downloadOne(inv)} className="inline-flex items-center gap-1 text-xs text-[#0A0A0A] hover:underline" data-testid={`billing-download-${inv.full_number}`}>
                      <Download className="w-3 h-3" /> XML
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-[#E8E4D9] flex items-center justify-between text-xs" data-testid="billing-pager">
            <div className="text-[#737373]">
              {page * BILLING_PAGE_SIZE + 1}–{Math.min((page + 1) * BILLING_PAGE_SIZE, items.length)} su {items.length}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                data-testid="billing-pager-prev"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#E8E4D9] bg-white disabled:opacity-40 hover:bg-[#F5F2EA] font-semibold"
              >
                <ArrowLeft className="w-3 h-3" /> Precedente
              </button>
              <span className="tabular-nums font-semibold">Pag. {page + 1} / {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page + 1 >= totalPages}
                data-testid="billing-pager-next"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#E8E4D9] bg-white disabled:opacity-40 hover:bg-[#F5F2EA] font-semibold"
              >
                Successiva <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
